import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Divider,
} from '@mui/material';
import { 
  ExpandMore, 
  GroupWork, 
  Sports, 
  CheckCircle, 
  RadioButtonUnchecked,
  AssignmentTurnedIn,
  Shuffle,
  DragIndicator,
  ListAlt,
  Schedule,
  AccountTree,
  Print,
  Refresh,
} from '@mui/icons-material';
import ViewBrackets from './draw/ViewBrackets';
import GenerateDraw from './draw/GenerateDraw';
import DrawStepper from './draw/DrawStepper';
import GeneralGroupings from './draw/GeneralGroupings';
import AdjustGroupings from './draw/AdjustGroupings';

interface DrawViewProps {
  wrestlers: Array<{ schoolName: string; firstName: string; lastName: string; weightKg: number; age?: number; grade?: number | string; gender?: string }>;
  weightUnit?: 'kg' | 'lbs';
  initialDrawState?: any;
  onDrawStateChange?: (drawState: any) => void;
}

// Local storage helper functions
const saveStateToLocalStorage = (state: any) => {
  try {
    localStorage.setItem('drawViewState', JSON.stringify(state));
  } catch (error) {
    console.error('Error saving state to localStorage:', error);
  }
};

const loadStateFromLocalStorage = () => {
  try {
    const savedState = localStorage.getItem('drawViewState');
    return savedState ? JSON.parse(savedState) : null;
  } catch (error) {
    console.error('Error loading state from localStorage:', error);
    return null;
  }
};

// Apply wrestler move history to restore custom group assignments
const applyMoveHistory = (weightClasses: any[], wrestlers: any[]) => {
  try {
    const moveHistoryStr = localStorage.getItem('wrestlerMoveHistory');
    if (!moveHistoryStr) {
      console.log('No move history found, using original weight classes');
      return weightClasses;
    }

    const moveHistory = JSON.parse(moveHistoryStr);
    console.log('Applying move history:', moveHistory.length, 'moves to apply');

    // Create a deep copy of the weight classes to modify
    let updatedClasses = JSON.parse(JSON.stringify(weightClasses));

    // Apply each move in chronological order
    moveHistory.forEach((move: any) => {
      console.log(`Applying move: ${move.wrestler.firstName} ${move.wrestler.lastName} from ${move.fromGroup} to ${move.toGroup}`);

      // Find the wrestler in the current classes
      let wrestlerFound = false;
      let wrestlerToMove: any = null;

      // Search for the wrestler across all classes
      for (let className in updatedClasses) {
        if (updatedClasses[className].wrestlers) {
          const wrestlerIndex = updatedClasses[className].wrestlers.findIndex((w: any) =>
            w.firstName === move.wrestler.firstName &&
            w.lastName === move.wrestler.lastName &&
            w.schoolName === move.wrestler.schoolName
          );

          if (wrestlerIndex !== -1) {
            wrestlerToMove = updatedClasses[className].wrestlers[wrestlerIndex];
            // Remove wrestler from current class
            updatedClasses[className].wrestlers.splice(wrestlerIndex, 1);
            wrestlerFound = true;
            break;
          }
        }
      }

      // If wrestler found, add to the target class
      if (wrestlerFound && wrestlerToMove) {
        // Find the target class
        for (let className in updatedClasses) {
          if (updatedClasses[className].name === move.toGroup) {
            updatedClasses[className].wrestlers.push(wrestlerToMove);
            console.log(`Successfully moved ${move.wrestler.firstName} ${move.wrestler.lastName} to ${move.toGroup}`);
            break;
          }
        }
      } else {
        console.warn(`Wrestler not found for move: ${move.wrestler.firstName} ${move.wrestler.lastName}`);
      }
    });

    console.log('Move history applied successfully');
    return updatedClasses;

  } catch (error) {
    console.error('Error applying move history:', error);
    return weightClasses; // Return original classes if there's an error
  }
};

const DrawView: React.FC<DrawViewProps> = ({ wrestlers, weightUnit = 'kg', initialDrawState, onDrawStateChange }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedWeightClass, setSelectedWeightClass] = useState<string>('');
  const [drawComplete, setDrawComplete] = useState(false);
  const [weightGroups, setWeightGroups] = useState<Array<{ name: string; wrestlers: any[]; brackets?: any[]; rounds?: any[]; weightClasses?: any[] }>>([]);
  const [weightDelta, setWeightDelta] = useState(2.5); // Default 2.5kg delta
  const [ageDelta, setAgeDelta] = useState(3); // Default 3 year delta for age grouping
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [separateGenders, setSeparateGenders] = useState(true); // Default to separate genders
  const [ageRangeSeparation, setAgeRangeSeparation] = useState(0); // Default 0 = no age separation

  // Load saved state from local storage on mount
  React.useEffect(() => {
    const savedState = loadStateFromLocalStorage();
    if (savedState) {
      // Always restore the tournament state
      setCurrentStep(savedState.currentStep || 0);
      setDrawComplete(savedState.drawComplete || false);
      setWeightGroups(savedState.weightGroups || []);
      if (savedState.adjustedWeightClasses) {
        setAdjustedWeightClasses(savedState.adjustedWeightClasses);
      }
      setWeightDelta(savedState.weightDelta || 2.5);
      setAgeDelta(savedState.ageDelta || 3);
      setSeparateGenders(savedState.separateGenders !== undefined ? savedState.separateGenders : true);
      setNumMats(savedState.numMats || 2);
    }
  }, [wrestlers.length]); // Run on mount and when wrestlers count changes

  const [adjustedWeightClasses, setAdjustedWeightClasses] = useState<Array<{ name: string; wrestlers: any[]; minWeight: number; maxWeight: number; gender?: string; format?: string; rounds?: any[] }>>([]);
  const [draggedWrestler, setDraggedWrestler] = useState<any>(null);
  const [dragOverClass, setDragOverClass] = useState<string>('');
  const [selectedGroupForMatches, setSelectedGroupForMatches] = useState<string>('');
  const [showMatchesDialog, setShowMatchesDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [numMats, setNumMats] = useState(2);

  // Save state to local storage when weight groups change
  React.useEffect(() => {
    if (adjustedWeightClasses.length > 0 || weightGroups.length > 0) {
      const stateToSave = {
        currentStep,
        drawComplete,
        weightGroups,
        adjustedWeightClasses,
        wrestlers,
        weightDelta,
        ageDelta,
        separateGenders,
        numMats
      };

      saveStateToLocalStorage(stateToSave);
      
      // Also notify parent component to save to app-level storage
      if (onDrawStateChange) {
        onDrawStateChange(stateToSave);
      }
    }
  }, [adjustedWeightClasses, weightGroups, onDrawStateChange]);
  const [showMatDialog, setShowMatDialog] = useState(false);
  const [showMatConfirmationDialog, setShowMatConfirmationDialog] = useState(false);
  const [tempNumMats, setTempNumMats] = useState(2);
  const [scheduledMatches, setScheduledMatches] = useState<Array<{
    boutNumber: number;
    match: any;
    mat: number;
    weightClass: string;
  }>>([]);
  const [originalScheduledMatches, setOriginalScheduledMatches] = useState<Array<{
    boutNumber: number;
    match: any;
    mat: number;
    weightClass: string;
  }>>([]);
  const [restTimeAnalysis, setRestTimeAnalysis] = useState<{
    flaggedMatches: Array<{
      boutNumber: number;
      match: any;
      mat: number;
      weightClass: string;
      roundNumber?: number;
    }>;
    wrestlerIssues: Array<{
      wrestler: string;
      issues: Array<{
        fromBout: number;
        toBout: number;
        matchesBetween: number;
        mat: number;
        weightClass: string;
      }>;
    }>;
    totalWrestlers: number;
    wrestlersWithIssues: number;
  } | null>(null);
  const [viewMode, setViewMode] = useState<'schedule' | 'brackets'>('schedule');
  const isUpdatingFromParent = useRef(false);
  const fallbackCounter = useRef(0);
  const isInitialMount = useRef(true);

  // Group format options
  const groupFormats = [
    { value: 'round-robin', label: 'Round Robin' },
    { value: 'double-elimination', label: 'Double Elimination' },
  ];
 /*
   const groupFormats = [
    { value: 'round-robin', label: 'Round Robin' },
    { value: 'double-elimination', label: 'Double Elimination' },
    { value: 'elimination', label: 'Single Elimination' },
    { value: 'pool-play', label: 'Pool Play' },
    { value: 'best-of-three', label: 'Best of 3' },
    { value: 'custom', label: 'Custom' }
  ];
 */



  // Handle group format change
  const handleGroupFormatChange = (className: string, format: string) => {
    const updatedClasses = adjustedWeightClasses.map(cls => {
      if (cls.name === className) {
        return { ...cls, format };
      }
      return cls;
    });
    setAdjustedWeightClasses(updatedClasses);
  };

  // Handle refresh group
  const handleRefreshGroup = () => {
    console.log('\n=== REFRESH GROUP CALLED ===');
    console.log('Current weight classes:', adjustedWeightClasses.map(wc => ({
      name: wc.name,
      wrestlers: wc.wrestlers.length,
      hasRounds: !!wc.rounds
    })));
    
    // Find the current weight class
    const currentWeightClass = adjustedWeightClasses.find(cls => 
      Object.keys(adjustedWeightClasses).some(key => key === selectedGroupForMatches)
    );
    
    console.log('Current weight class found:', currentWeightClass?.name || 'Not found');
    
    if (currentWeightClass && currentWeightClass.format === 'round-robin') {
      // For round robin, generate round-based matches
      const updatedRounds = generateRoundRobinMatches(currentWeightClass.wrestlers);
      console.log('Round robin refresh: generating rounds for', currentWeightClass.name, 'with', updatedRounds.length, 'rounds');
      
      // Log the rounds structure
      console.log('Generated rounds structure:');
      updatedRounds.forEach((round, index) => {
        console.log(`Round ${round.roundNumber}: ${round.matches.length} matches, ${round.byes.length} byes`);
      });
      
      // Update the weight class with new rounds
      const updatedClasses = adjustedWeightClasses.map(cls => {
        if (cls.name === currentWeightClass.name) {
          return { ...cls, rounds: updatedRounds };
        }
        return cls;
      });
      
      setAdjustedWeightClasses(updatedClasses);
      console.log('Round robin refresh: updated weight class with rounds');
    } else {
      // For other formats, just log the action
      console.log('Refresh called for non-round-robin format:', currentWeightClass?.name, currentWeightClass?.format);
    }
  };

  // Restore draw state from props if provided (only when actually needed)
  useEffect(() => {
    if (initialDrawState && Object.keys(initialDrawState).length > 0 && !isUpdatingFromParent.current) {
      isUpdatingFromParent.current = true;
      
      
      // Always restore step on initial mount
      if (isInitialMount.current && initialDrawState.currentStep !== undefined) {
        setCurrentStep(initialDrawState.currentStep);
      }
      
      // Always restore weight groups if they exist in the saved state
      if (initialDrawState.weightGroups && initialDrawState.weightGroups.length > 0) {
        // Ensure wrestlers have weightKg property when restoring
        const restoredWeightGroups = initialDrawState.weightGroups.map((group: any) => ({
          ...group,
          wrestlers: group.wrestlers.map((w: any) => {
            // If wrestler is already a complete object, keep it as is
            if (w && typeof w === 'object' && w.firstName && w.lastName && w.weightKg !== undefined) {
              return w;
            }
            // If wrestler is just a string ID, find the full wrestler object
            if (typeof w === 'string') {
              const wrestler = wrestlers.find(wrestler => 
                wrestler.firstName + '_' + wrestler.lastName === w
              );
              if (wrestler) {
                return wrestler;
              }
              // Create a unique fallback wrestler to avoid duplicates
              fallbackCounter.current += 1;
              return { 
                firstName: 'Unknown', 
                lastName: `Wrestler${Date.now()}_${fallbackCounter.current}`, 
                weightKg: 0,
                schoolName: 'Unknown'
              };
            }
            // If wrestler object is incomplete but has firstName/lastName, try to find full object
            if (w && typeof w === 'object' && w.firstName && w.lastName) {
              const wrestler = wrestlers.find(wrestler => 
                wrestler.firstName === w.firstName && wrestler.lastName === w.lastName
              );
              if (wrestler) {
                return wrestler;
              }
              // Return the object with weightKg if missing
              return {
                ...w,
                weightKg: w.weightKg || 0,
                schoolName: w.schoolName || 'Unknown'
              };
            }
            // Fallback for any other case
            fallbackCounter.current += 1;
            return { 
              firstName: 'Unknown', 
              lastName: `Wrestler${Date.now()}_${fallbackCounter.current}`, 
              weightKg: 0,
              schoolName: 'Unknown'
            };
          })
        }));
        setWeightGroups(restoredWeightGroups);
        
        // Also restore step when weight groups are restored (returning from other views)
        if (initialDrawState.currentStep !== undefined) {
          setCurrentStep(initialDrawState.currentStep);
        }
      }
      
      // Always restore adjusted weight classes if they exist in the saved state
      if (initialDrawState.adjustedWeightClasses && initialDrawState.adjustedWeightClasses.length > 0) {
        // Ensure wrestlers have weightKg property when restoring
        const restoredAdjustedClasses = initialDrawState.adjustedWeightClasses.map((cls: any) => ({
          ...cls,
          wrestlers: cls.wrestlers.map((w: any) => {
            // If wrestler is already a complete object, keep it as is
            if (w && typeof w === 'object' && w.firstName && w.lastName && w.weightKg !== undefined) {
              return w;
            }
            // If wrestler is just a string ID, find the full wrestler object
            if (typeof w === 'string') {
              const wrestler = wrestlers.find(wrestler => 
                wrestler.firstName + '_' + wrestler.lastName === w
              );
              if (wrestler) {
                return wrestler;
              }
              // Create a unique fallback wrestler to avoid duplicates
              fallbackCounter.current += 1;
              return { 
                firstName: 'Unknown', 
                lastName: `Wrestler${Date.now()}_${fallbackCounter.current}`, 
                weightKg: 0,
                schoolName: 'Unknown'
              };
            }
            // If wrestler object is incomplete but has firstName/lastName, try to find full object
            if (w && typeof w === 'object' && w.firstName && w.lastName) {
              const wrestler = wrestlers.find(wrestler => 
                wrestler.firstName === w.firstName && wrestler.lastName === w.lastName
              );
              if (wrestler) {
                return wrestler;
              }
              // Return the object with weightKg if missing
              return {
                ...w,
                weightKg: w.weightKg || 0,
                schoolName: w.schoolName || 'Unknown'
              };
            }
            // Fallback for any other case
            fallbackCounter.current += 1;
            return { 
              firstName: 'Unknown', 
              lastName: `Wrestler${Date.now()}_${fallbackCounter.current}`, 
              weightKg: 0,
              schoolName: 'Unknown'
            };
          })
        }));
        setAdjustedWeightClasses(restoredAdjustedClasses);
        
        // Also restore step when adjusted classes are restored (returning from other views)
        if (initialDrawState.currentStep !== undefined) {
          setCurrentStep(initialDrawState.currentStep);
        }
      }
      
      // Only restore other state if it's different from current state
      if (initialDrawState.drawComplete !== undefined && initialDrawState.drawComplete !== drawComplete) {
        setDrawComplete(initialDrawState.drawComplete);
      }
      if (initialDrawState.weightDelta !== undefined && initialDrawState.weightDelta !== weightDelta) {
        setWeightDelta(initialDrawState.weightDelta);
      }
      if (initialDrawState.separateGenders !== undefined && initialDrawState.separateGenders !== separateGenders) {
        setSeparateGenders(initialDrawState.separateGenders);
      }
      if (initialDrawState.activeTab !== undefined && initialDrawState.activeTab !== activeTab) {
        setActiveTab(initialDrawState.activeTab);
      }
      if (initialDrawState?.numMats !== undefined && initialDrawState.numMats !== numMats) {
        setNumMats(initialDrawState.numMats);
      }
      if (initialDrawState.viewMode && initialDrawState.viewMode !== viewMode) {
        setViewMode(initialDrawState.viewMode);
      }
      
      // Reset the flag after a short delay
      setTimeout(() => {
        isUpdatingFromParent.current = false;
        isInitialMount.current = false;
      }, 50);
    }
  }, [initialDrawState, wrestlers, drawComplete, weightDelta, separateGenders, activeTab, viewMode]);

  // Sync draw state to App.tsx when it changes (prevent infinite loop)
  useEffect(() => {
    if (onDrawStateChange && !isUpdatingFromParent.current) {
      // Only send state if we have meaningful data to save
      const hasWeightGroups = weightGroups.length > 0;
      const hasAdjustedClasses = adjustedWeightClasses.length > 0;
      const hasStepData = currentStep > 0 || drawComplete;
      
      // Only sync if we have actual data to save
      if (hasWeightGroups || hasAdjustedClasses || hasStepData) {
        const drawState = {
          currentStep,
          drawComplete,
          weightGroups: weightGroups.map(group => ({
            ...group,
            wrestlers: group.wrestlers.map(w => {
              // Preserve full wrestler object with all properties
              if (w && typeof w === 'object' && w.firstName && w.lastName) {
                return {
                  id: w.id || w.firstName + '_' + w.lastName,
                  firstName: w.firstName,
                  lastName: w.lastName,
                  weightKg: w.weightKg,
                  schoolName: w.schoolName,
                  age: w.age,
                  grade: w.grade,
                  gender: w.gender
                };
              }
              return w;
            })
          })),
          weightDelta,
          separateGenders,
          adjustedWeightClasses: adjustedWeightClasses.map(cls => ({
            ...cls,
            wrestlers: cls.wrestlers.map(w => {
              // Preserve full wrestler object with all properties
              if (w && typeof w === 'object' && w.firstName && w.lastName) {
                return {
                  id: w.id || w.firstName + '_' + w.lastName,
                  firstName: w.firstName,
                  lastName: w.lastName,
                  weightKg: w.weightKg,
                  schoolName: w.schoolName,
                  age: w.age,
                  grade: w.grade,
                  gender: w.gender
                };
              }
              return w;
            })
          })),
          activeTab,
          numMats,
          scheduledMatches: scheduledMatches.map(match => ({
            boutNumber: match.boutNumber,
            wrestler1Id: match.match.wrestler1?.id || match.match.wrestler1?.firstName + '_' + match.match.wrestler1?.lastName,
            wrestler2Id: match.match.wrestler2?.id || match.match.wrestler2?.firstName + '_' + match.match.wrestler2?.lastName,
            mat: match.mat,
            weightClass: match.weightClass
          })),
          viewMode
        };
        
        console.log('DrawView: Sending state to App.tsx', {
          currentStep,
          weightGroupsCount: weightGroups.length,
          adjustedWeightClassesCount: adjustedWeightClasses.length,
          weightGroups: weightGroups.map(g => ({ name: g.name, wrestlersCount: g.wrestlers.length })),
          adjustedWeightClasses: adjustedWeightClasses.map(c => ({ name: c.name, wrestlersCount: c.wrestlers.length })),
          isUpdatingFromParent: isUpdatingFromParent.current,
          hasWeightGroups,
          hasAdjustedClasses,
          hasStepData
        });
        
        onDrawStateChange(drawState);
      }
    }
  }, [currentStep, drawComplete, weightGroups, adjustedWeightClasses, weightDelta, separateGenders, activeTab, numMats, scheduledMatches, viewMode, onDrawStateChange]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const CustomTabPanel = (props: { children?: React.ReactNode; index: number; value: number }) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups for this site to use the print feature');
      return;
    }

    let printContent = '';

    if (viewMode === 'schedule' && scheduledMatches.length > 0) {
      // Generate schedule content
      printContent = `
        <html>
          <head>
            <title>Tournament Schedule</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .mat-section { page-break-inside: avoid; margin-bottom: 30px; border: 1px solid #ccc; padding: 15px; }
              .mat-title { font-size: 18px; font-weight: bold; color: #89479a; margin-bottom: 10px; }
              .weight-classes { margin-bottom: 15px; }
              .weight-class { display: inline-block; background: #f8e6ff; border: 1px solid #89479a; padding: 4px 8px; margin: 2px; border-radius: 12px; font-size: 12px; }
              .match { border: 1px solid #ddd; margin: 8px 0; padding: 10px; background: #f9f9f9; }
              .match-number { font-weight: bold; color: #89479a; }
              .match-weight-class { float: right; background: #fff4e6; border: 1px solid #f78f29; padding: 2px 6px; border-radius: 8px; font-size: 11px; }
              .wrestlers { font-size: 14px; }
              @media print { .mat-section { page-break-inside: avoid; } }
            </style>
          </head>
          <body>
            <h1>Tournament Schedule</h1>
            <p>Total matches: ${scheduledMatches.length} across ${numMats} mat(s)</p>
            ${Array.from({ length: numMats }, (_, matIndex) => {
              const matMatches = scheduledMatches.filter(match => match.mat === matIndex + 1);
              const matGroups = Array.from(new Set(matMatches.map(m => m.weightClass)));
              
              return `
                <div class="mat-section">
                  <div class="mat-title">Mat ${matIndex + 1}</div>
                  <div class="weight-classes">
                    <strong>Weight Classes (${matGroups.length}):</strong><br>
                    ${matGroups.map(groupName => `<span class="weight-class">${groupName}</span>`).join('')}
                  </div>
                  <div>
                    <strong>Matches (${matMatches.length}):</strong><br>
                    ${matMatches.map((scheduledMatch) => `
                      <div class="match">
                        <div>
                          <span class="match-number">Match #${scheduledMatch.boutNumber}</span>
                          <span>${scheduledMatch.weightClass === 'REST PERIOD' ? 
                                        'REST PERIOD - 5 Minutes' : 
                                        `${scheduledMatch.match.wrestler1.firstName} ${scheduledMatch.match.wrestler1.lastName} (${scheduledMatch.match.wrestler1.schoolName || 'Unknown'}) vs ${scheduledMatch.match.wrestler2.firstName} ${scheduledMatch.match.wrestler2.lastName} (${scheduledMatch.match.wrestler2.schoolName || 'Unknown'})`
                                  }
                          </span>
                          
                          
                          <span class="match-weight-class">${scheduledMatch.weightClass}</span>
                        </div>
                        <div class="wrestlers">
                          
                        </div>
                      </div>
                    `).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </body>
        </html>
      `;
    } else if (viewMode === 'brackets') {
      // Generate brackets content
      printContent = `
        <html>
          <head>
            <title>Tournament Brackets</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .weight-class-section { page-break-after: always; margin-bottom: 30px; border: 1px solid #ccc; padding: 15px; min-height: 80vh; }
              .weight-class-section:last-child { page-break-after: auto; }
              .weight-class-title { font-size: 18px; font-weight: bold; color: #89479a; margin-bottom: 10px; }
              .weight-class-info { font-size: 14px; color: #666; margin-bottom: 15px; }
              .matches-section { margin-bottom: 15px; }
              .match { border: 1px solid #ddd; margin: 8px 0; padding: 10px; background: #f9f9f9; }
              .wrestlers { font-size: 14px; }
              @media print { 
                .weight-class-section { page-break-after: always; page-break-inside: avoid; }
                .weight-class-section:last-child { page-break-after: auto; }
              }
            </style>
          </head>
          <body>
            ${adjustedWeightClasses.map((weightClass) => {
              const matches = generateMatches(weightClass.wrestlers);
              const format = weightClass.wrestlers.length === 2 ? "Best of 3 format" : 
                            weightClass.wrestlers.length === 3 ? "Round robin format" : 
                            "Single elimination format";
              
              // Handle 1-competitor case
              const champion = weightClass.wrestlers.length === 1 ? weightClass.wrestlers[0] : null;
              
              return `
                <div class="weight-class-section">
                  <div class="weight-class-title">${weightClass.name}</div>
                  <div class="weight-class-info">
                    ${weightClass.wrestlers.length} wrestlers (${weightClass.minWeight.toFixed(1)}-${weightClass.maxWeight.toFixed(1)} kg)<br>
                    <strong>Format:</strong> ${format}
                  </div>
                  ${champion ? `
                    <div class="matches-section">
                      <strong>Champion:</strong><br>
                      <div class="match">
                        <div class="wrestlers">
                          ${champion.firstName} ${champion.lastName} (${champion.schoolName || 'N/A'})
                        </div>
                      </div>
                    </div>
                  ` : `
                    <div class="matches-section">
                      <strong>Required Matches (${matches.length}):</strong><br>
                      ${matches.map((match, index) => `
                        <div class="match">
                          <div class="wrestlers">
                            ${match.wrestler1.firstName} ${match.wrestler1.lastName} (${match.wrestler1.schoolName || 'N/A'}) vs ${match.wrestler2.firstName} ${match.wrestler2.lastName} (${match.wrestler2.schoolName || 'N/A'})
                          </div>
                        </div>
                      `).join('')}
                    </div>
                  `}
                </div>
              `;
            }).join('')}
          </body>
        </html>
      `;
    }

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleScheduleMatches = () => {
    console.log('\n=== SCHEDULING MATCHES ===');
    console.log('Number of mats:', numMats);
    console.log('Number of weight classes:', adjustedWeightClasses.length);

    // Step 1: Generate round-based matches for all weight classes
    const weightClassesWithRounds = adjustedWeightClasses.map(weightClass => {
      const rounds = generateRoundRobinMatches(weightClass.wrestlers);
      const totalMatches = rounds.reduce((sum, r) => sum + r.matches.length, 0);
      console.log(`Weight class ${weightClass.name}: ${rounds.length} rounds, ${totalMatches} total matches`);
      return { ...weightClass, rounds, totalMatches };
    });

    // Step 2: Assign weight classes to mats to balance total matches
    const matAssignments: Array<{
      mat: number;
      weightClasses: typeof weightClassesWithRounds;
      totalMatches: number;
    }> = Array.from({ length: numMats }, (_, i) => ({
      mat: i + 1,
      weightClasses: [],
      totalMatches: 0
    }));

    // Sort weight classes by total matches (descending) for better balancing
    const sortedWeightClasses = [...weightClassesWithRounds].sort((a, b) => b.totalMatches - a.totalMatches);

    // Assign weight classes to mats using greedy algorithm for balance
    sortedWeightClasses.forEach(weightClass => {
      // Find mat with least matches
      const leastLoadedMat = matAssignments.reduce((min, current) => 
        current.totalMatches < min.totalMatches ? current : min
      );
      
      leastLoadedMat.weightClasses.push(weightClass);
      leastLoadedMat.totalMatches += weightClass.totalMatches;
      console.log(`Assigned ${weightClass.name} (${weightClass.totalMatches} matches) to Mat ${leastLoadedMat.mat}`);
    });

    console.log('\nMat assignments:');
    matAssignments.forEach(ma => {
      console.log(`Mat ${ma.mat}: ${ma.weightClasses.length} weight classes, ${ma.totalMatches} total matches`);
      ma.weightClasses.forEach(wc => {
        console.log(`  - ${wc.name}: ${wc.totalMatches} matches`);
      });
    });

    // Step 3: Schedule matches using DrawContext staggered pattern
    const scheduledMatches: Array<{
      boutNumber: number;
      match: any;
      mat: number;
      weightClass: string;
      roundNumber: number;
      isRest: boolean;
    }> = [];

    // Create a global schedule following DrawContext pattern
    const globalSchedule: Array<{
      roundNumber: number;
      weightClasses: Array<{
        name: string;
        matAssignment: number;
        roundIndex: number;
        matches: any[];
      }>;
    }> = [];

    // Find the maximum number of rounds across all weight classes
    const maxRounds = Math.max(...matAssignments.flatMap(ma => 
      ma.weightClasses.map(wc => wc.rounds?.length || 0)
    ));

    console.log(`Maximum rounds across all weight classes: ${maxRounds}`);

    // Build the staggered schedule according to DrawContext
    for (let roundNum = 1; roundNum <= maxRounds; roundNum++) {
      console.log(`\n=== GLOBAL ROUND ${roundNum} ===`);
      
      const roundWeightClasses: Array<{
        name: string;
        matAssignment: number;
        roundIndex: number;
        matches: any[];
      }> = [];

      // Add weight classes that should compete in this round according to DrawContext pattern
      matAssignments.forEach(matAssignment => {
        matAssignment.weightClasses.forEach(weightClass => {
          const totalRounds = weightClass.rounds?.length || 0;
          
          // DrawContext scheduling logic:
          // Groups with 6 rounds: Every round (1,2,3,4,5,6)
          // Groups with 5 rounds: Every round (1,2,3,4,5)
          // Groups with 4 rounds: Rounds 1,2,3,4
          // Groups with 3 rounds: Rounds 1,3
          // Groups with 2 rounds: Rounds 1,2
          
          let shouldScheduleThisRound = false;
          let roundIndex = roundNum;
          
          if (totalRounds === 6) {
            shouldScheduleThisRound = roundNum <= 6;
          } else if (totalRounds === 5) {
            shouldScheduleThisRound = roundNum <= 5;
          } else if (totalRounds === 4) {
            shouldScheduleThisRound = roundNum <= 4;
          } else if (totalRounds === 3) {
            shouldScheduleThisRound = roundNum === 1 || roundNum === 3;
          } else if (totalRounds === 2) {
            shouldScheduleThisRound = roundNum === 1 || roundNum === 2;
          }
          
          if (shouldScheduleThisRound && roundIndex <= totalRounds) {
            const currentRound = weightClass.rounds?.find(r => r.roundNumber === roundIndex);
            
            if (currentRound && currentRound.matches.length > 0) {
              console.log(`Adding ${weightClass.name} (${totalRounds} rounds) to Global Round ${roundNum}`);
              roundWeightClasses.push({
                name: weightClass.name,
                matAssignment: matAssignment.mat,
                roundIndex: roundIndex,
                matches: currentRound.matches
              });
            }
          }
        });
      });

      globalSchedule.push({
        roundNumber: roundNum,
        weightClasses: roundWeightClasses
      });
    }

    // Track bout numbers per mat
    const matBoutCounters: Record<number, number> = {};
    matAssignments.forEach(ma => {
      matBoutCounters[ma.mat] = 0;
    });

    // Now convert the global schedule to actual scheduled matches
    globalSchedule.forEach((globalRound) => {
      console.log(`\n--- SCHEDULING GLOBAL ROUND ${globalRound.roundNumber} ---`);
      
      globalRound.weightClasses.forEach(weightClassInfo => {
        weightClassInfo.matches.forEach((match: any) => {
          const mat = weightClassInfo.matAssignment;
          matBoutCounters[mat] = (matBoutCounters[mat] || 0) + 1;
          
          scheduledMatches.push({
            boutNumber: matBoutCounters[mat],
            match,
            mat,
            weightClass: weightClassInfo.name,
            roundNumber: globalRound.roundNumber,
            isRest: weightClassInfo.name === 'REST PERIOD'
          });
        });
      });
    });

    console.log('\nFinal scheduled matches:', scheduledMatches.length);
    console.log('Sample scheduled matches:', scheduledMatches.slice(0, 15).map(sm => ({
      boutNumber: sm.boutNumber,
      mat: sm.mat,
      round: sm.roundNumber,
      weightClass: sm.weightClass,
      match: `${sm.match.wrestler1.firstName} vs ${sm.match.wrestler2.firstName}`
    })));

    setScheduledMatches(scheduledMatches);
    setOriginalScheduledMatches(scheduledMatches); // Save original schedule for reset
    
    // Analyze rest times for all wrestlers
    const analysis = analyzeWrestlerRestTimes(scheduledMatches);
    setRestTimeAnalysis(analysis);
    
    setShowMatDialog(false);
  };

  // Handle Add Rest functionality
  const handleAddRest = () => {
    if (!restTimeAnalysis || restTimeAnalysis.flaggedMatches.length === 0) {
      console.log('No rest violations to fix');
      return;
    }

    // Recursive function to add rest periods iteratively
    const addRestPeriods = (currentScheduledMatches: any[], currentAnalysis: any, iteration: number = 0) => {
      // Prevent infinite loops - safety check
      if (iteration > 100) {
        console.log('Safety limit reached - stopping rest period additions');
        return;
      }

      if (!currentAnalysis || currentAnalysis.flaggedMatches.length === 0) {
        console.log('All rest violations resolved - no more flagged matches');
        console.log('Add Rest process completed successfully');
        return;
      }

      console.log(`\n=== Iteration ${iteration + 1} ===`);
      console.log(`Current flagged matches: ${currentAnalysis.flaggedMatches.length}`);

      // Find first flagged match, starting from mat 1 and progressing through mats
      let firstFlaggedMatch: {
        boutNumber: number;
        match: any;
        mat: number;
        weightClass: string;
        roundNumber?: number;
      } | null = null;
      
      // Check mats in order: 1, 2, 3, etc.
      for (let matNum = 1; matNum <= 8; matNum++) { // Assuming max 8 mats
        const flaggedMatch = currentAnalysis.flaggedMatches.find(
          (match: any) => match.mat === matNum
        );
        
        if (flaggedMatch) {
          firstFlaggedMatch = flaggedMatch;
          break;
        }
      }

      if (!firstFlaggedMatch) {
        console.log('No flagged matches found on any mat');
        return;
      }

      console.log(`Adding rest before match ${firstFlaggedMatch.boutNumber} on mat ${firstFlaggedMatch.mat}`);

      // Create a rest match (placeholder) to insert before the flagged match
      const restMatch = {
        boutNumber: -1, // Temporary bout number, will be recalculated
        match: {
          wrestler1: { firstName: 'REST', lastName: 'PERIOD', schoolName: '' },
          wrestler2: { firstName: 'REST', lastName: 'PERIOD', schoolName: '' }
        },
        mat: firstFlaggedMatch.mat,
        weightClass: 'REST PERIOD',
        roundNumber: firstFlaggedMatch.roundNumber
      };

      // Insert the rest match before the flagged match
      const updatedScheduledMatches = [...currentScheduledMatches];
      const insertIndex = updatedScheduledMatches.findIndex(
        match => match.mat === firstFlaggedMatch!.mat && match.boutNumber === firstFlaggedMatch!.boutNumber
      );

      if (insertIndex !== -1) {
        updatedScheduledMatches.splice(insertIndex, 0, restMatch);
        
        // Recalculate bout numbers for the affected mat
        let matBoutNumber = 1;
        updatedScheduledMatches.forEach(match => {
          if (match.mat === firstFlaggedMatch!.mat) {
            match.boutNumber = matBoutNumber++;
          }
        });

        // Update state with the new schedule
        setScheduledMatches(updatedScheduledMatches);
        
        // Re-analyze rest times with the updated schedule
        const newAnalysis = analyzeWrestlerRestTimes(updatedScheduledMatches);
        setRestTimeAnalysis(newAnalysis);
        
        console.log(`Rest period added successfully. Remaining flagged matches: ${newAnalysis.flaggedMatches.length}`);
        
        // Continue iterating if there are still flagged matches
        if (newAnalysis.flaggedMatches.length > 0) {
          console.log('Still have violations - continuing to next iteration...');
          // Use setTimeout to allow UI to update between iterations
          setTimeout(() => {
            addRestPeriods(updatedScheduledMatches, newAnalysis, iteration + 1);
          }, 200); // Small delay to prevent blocking the UI
        } else {
          console.log('✅ All rest violations resolved! Add Rest process completed.');
        }
      }
    };

    // Start the iterative process
    console.log('Starting Add Rest process...');
    addRestPeriods(scheduledMatches, restTimeAnalysis);
  };

  // Handle mat confirmation when returning to step 3
  const handleStep3Entry = () => {
    if (scheduledMatches.length > 0) {
      // User is returning to step 3 with existing schedule
      setTempNumMats(numMats);
      setShowMatConfirmationDialog(true);
    } else {
      // First time entering step 3, proceed normally
      setCurrentStep(2);
    }
  };

  const handleMatConfirmation = () => {
    setShowMatConfirmationDialog(false);
    
    if (tempNumMats !== numMats) {
      // User changed the number of mats, need to regenerate schedule
      console.log(`Number of mats changed from ${numMats} to ${tempNumMats}, regenerating schedule`);
      setNumMats(tempNumMats);
      
      // Clear current schedule and regenerate
      setScheduledMatches([]);
      setRestTimeAnalysis(null);
      
      // Auto-generate new schedule with new mat count
      setTimeout(() => {
        handleScheduleMatches();
      }, 100);
    }
    
    setCurrentStep(2);
  };

  const handleMatConfirmationCancel = () => {
    setShowMatConfirmationDialog(false);
    setCurrentStep(2); // Still proceed to step 3 with original mats
  };

  // Handle Reset functionality
  const handleScheduleReset = () => {
    if (!originalScheduledMatches || originalScheduledMatches.length === 0) {
      console.log('No original schedule to reset to');
      return;
    }

    console.log('Resetting to original schedule...');
    
    // Clear current schedule first to force UI update
    setScheduledMatches([]);
    setRestTimeAnalysis(null);
    
    // Small delay to ensure UI updates, then restore original schedule
    setTimeout(() => {
      setScheduledMatches([...originalScheduledMatches]);
      
      // Re-analyze rest times with the restored schedule
      const analysis = analyzeWrestlerRestTimes(originalScheduledMatches);
      setRestTimeAnalysis(analysis);
      
      console.log('Schedule reset to original state');
    }, 100);
  };

  // Analyze scheduled matches for wrestler rest times
  const analyzeWrestlerRestTimes = (matches: Array<{
    boutNumber: number;
    match: any;
    mat: number;
    weightClass: string;
    roundNumber?: number;
  }>) => {


    console.log('\n=== ANALYZING WRESTLER REST TIMES ===');
    console.log('Total matches to analyze:', matches.length);
    console.log('Matches:', matches.map(m => ({
      bout: m.boutNumber,
      mat: m.mat,
      weight: m.weightClass,
      wrestlers: `${m.match.wrestler1?.firstName || 'Unknown'} vs ${m.match.wrestler2?.firstName || 'Unknown'}`
    })));
    
    // Create a map to track each wrestler's match sequence
    const wrestlerMatches: { [key: string]: Array<{
      boutNumber: number;
      match: any;
      mat: number;
      weightClass: string;
      roundNumber?: number;
    }> } = {};
    
    // Collect all matches for each wrestler
    matches.forEach((scheduledMatch) => {
      const match = scheduledMatch.match;
      const boutNumber = scheduledMatch.boutNumber;
      const mat = scheduledMatch.mat;
      const weightClass = scheduledMatch.weightClass;
      const roundNumber = scheduledMatch.roundNumber;
      console.log("MAT", scheduledMatch.mat, scheduledMatch.boutNumber)

      // Process wrestler 1
      const wrestler1Key = `${match.wrestler1.firstName}_${match.wrestler1.lastName}_${match.wrestler1.schoolName}`;
      if (!wrestlerMatches[wrestler1Key]) {
        wrestlerMatches[wrestler1Key] = [];
      }
      wrestlerMatches[wrestler1Key].push({ boutNumber, mat, weightClass, match, roundNumber });
      
      // Process wrestler 2
      const wrestler2Key = `${match.wrestler2.firstName}_${match.wrestler2.lastName}_${match.wrestler2.schoolName}`;
      if (!wrestlerMatches[wrestler2Key]) {
        wrestlerMatches[wrestler2Key] = [];
      }
      wrestlerMatches[wrestler2Key].push({ boutNumber, mat, weightClass, match, roundNumber });
    });
    
    // Sort each wrestler's matches by bout number
    Object.keys(wrestlerMatches).forEach(wrestlerKey => {
      wrestlerMatches[wrestlerKey].sort((a, b) => a.boutNumber - b.boutNumber);
    });
    
    // Analyze rest times and flag issues
    const flaggedMatches: Array<{
      boutNumber: number;
      match: any;
      mat: number;
      weightClass: string;
      roundNumber?: number;
    }> = [];
    const wrestlerIssues: Array<{
      wrestler: string;
      issues: Array<{
        fromBout: number;
        toBout: number;
        matchesBetween: number;
        mat: number;
        weightClass: string;
      }>;
    }> = [];
    
    Object.entries(wrestlerMatches).forEach(([wrestlerKey, matchSequence]) => {
      const issues = [];
      var lastRestBout = 0;
      var lastRestMat = 0;
      
      for (let i = 1; i < matchSequence.length; i++) {
        const prevMatch = matchSequence[i - 1];
        const currentMatch = matchSequence[i];
        
        // Calculate matches between consecutive bouts
        const matchesBetween = currentMatch.boutNumber - prevMatch.boutNumber - 1;
        const isRest = currentMatch.weightClass === 'REST PERIOD';
   

        if (matchesBetween < 4 && !isRest) {
          console.log(`⚠️  INSUFFICIENT REST MAT#${currentMatch.mat} ${currentMatch.boutNumber}: ${wrestlerKey.replace(/_/g, ' ')}`);
          console.log(`   From bout ${prevMatch.boutNumber} to bout ${currentMatch.boutNumber}: only ${matchesBetween} matches between (minimum 4 required)`);
                    

          var hasRestBetween = false;
          if(scheduledMatches?.length > 0){
            console.log(`scheduledMatches ${scheduledMatches?.length}`);
            for (let j = 1; j <= 4; j++) {
              const prevMatchOnSameMat = scheduledMatches.find(m => 
                m.mat === currentMatch.mat && m.boutNumber === currentMatch.boutNumber - j
              );

              if(prevMatchOnSameMat == undefined || prevMatchOnSameMat.weightClass === 'REST PERIOD'){
                console.log(`No match found for bout ${currentMatch.boutNumber - j} on mat ${currentMatch.mat}`);
                console.log(`No match found for prev ${prevMatchOnSameMat}`);
                hasRestBetween = true;
              }else{
                console.log(`Checking bout ${prevMatchOnSameMat?.boutNumber} on mat ${prevMatchOnSameMat?.mat}: ${prevMatchOnSameMat?.weightClass}`);
              }
                      
            }
          }

          if(hasRestBetween) {
            console.log(`✅ REST FOUND MAT#${currentMatch.mat} ${currentMatch.boutNumber}: ${wrestlerKey.replace(/_/g, ' ')}`);
          }else{
            // Flag impacted match , Not previous
            if (flaggedMatches.findIndex(m => m.boutNumber === currentMatch.boutNumber && m.mat === currentMatch.mat) === -1) {
              flaggedMatches.push({
                boutNumber: currentMatch.boutNumber,
                match: currentMatch,
                mat: currentMatch.mat,
                weightClass: currentMatch.weightClass
              });
            }

            issues.push({
              fromBout: prevMatch.boutNumber,
              toBout: currentMatch.boutNumber,
              matchesBetween,
              mat: currentMatch.mat,
              weightClass: currentMatch.weightClass
            });
          }

        }
      }
      
      if (issues.length > 0) {
        wrestlerIssues.push({
          wrestler: wrestlerKey.replace(/_/g, ' '),
          issues
        });
      }
    });
    
    console.log('\n=== REST TIME ANALYSIS SUMMARY ===');
    console.log(`Total wrestlers analyzed: ${Object.keys(wrestlerMatches).length}`);
    console.log(`Wrestlers with insufficient rest: ${wrestlerIssues.length}`);
    console.log(`Flagged matches: ${flaggedMatches.length}`);
    
    if (wrestlerIssues.length > 0) {
      console.log('\nWrestlers with insufficient rest:');
      wrestlerIssues.forEach(issue => {
        console.log(`- ${issue.wrestler}:`);
        issue.issues.forEach(restIssue => {
          console.log(`  * Bout ${restIssue.fromBout} → ${restIssue.toBout}: ${restIssue.matchesBetween} matches between (Mat ${restIssue.mat}, ${restIssue.weightClass})`);
        });
      });
    }
    
    return {
      flaggedMatches: flaggedMatches.sort((a, b) => a.boutNumber - b.boutNumber),
      wrestlerIssues,
      totalWrestlers: Object.keys(wrestlerMatches).length,
      wrestlersWithIssues: wrestlerIssues.length
    };
  };

  // Create weight classes based on actual wrestler weights and configurable delta
  const createWeightClasses = () => {
    if (wrestlers.length === 0) return [];
    
    const classes: Array<{ name: string; wrestlers: any[]; minWeight: number; maxWeight: number; gender?: string; ageGroup?: string }> = [];
    
    // Step 1: Create weight classes first
    if (separateGenders) {
      // Separate by gender first
      const maleWrestlers = wrestlers.filter(w => w.gender === 'M');
      const femaleWrestlers = wrestlers.filter(w => w.gender === 'F');
      
      // Step 2: Create weight classes for males, then subdivide by age/grade
      if (maleWrestlers.length > 0) {
        const maleWeights = maleWrestlers.map(w => w.weightKg).sort((a, b) => a - b);
        const maleMinWeight = Math.floor(maleWeights[0] / weightDelta) * weightDelta;
        const maleMaxWeight = Math.ceil(maleWeights[maleWeights.length - 1] / weightDelta) * weightDelta;
        
        for (let weight = maleMinWeight; weight <= maleMaxWeight; weight += weightDelta) {
          const weightClassWrestlers = maleWrestlers.filter(w => 
            w.weightKg >= weight && w.weightKg < weight + weightDelta
          );
          
          if (weightClassWrestlers.length > 0) {
            // Step 3: Subdivide by age/grade within this weight class
            const ageGroups: { [key: string]: typeof weightClassWrestlers } = {};
            
            weightClassWrestlers.forEach((wrestler: any) => {
              let numericGrade: number | undefined;
              
              if (wrestler.grade !== undefined) {
                if (typeof wrestler.grade === 'number') {
                  numericGrade = wrestler.grade;
                } else if (typeof wrestler.grade === 'string') {
                  const extractedGrade = parseInt(wrestler.grade.replace(/\D/g, ''));
                  if (!isNaN(extractedGrade)) {
                    numericGrade = extractedGrade;
                  }
                }
              } else if (wrestler.age !== undefined && typeof wrestler.age === 'number') {
                // Use age as grade approximation (age + 3 = typical grade)
                numericGrade = wrestler.age + 3;
              }
              
              let grade = 'Unknown';
              if (numericGrade !== undefined) {
                // Group grades by ageDelta ranges
                const gradeGroup = Math.floor((numericGrade - 1) / ageDelta) * ageDelta + 1;
                const gradeGroupEnd = Math.min(gradeGroup + ageDelta - 1, 8); // Cap max grade at 9
                
                // Create grade range label
                if (ageDelta === 1) {
                  grade = `Grade ${numericGrade}`;
                } else {
                  const grades = [];
                  for (let g = gradeGroup; g <= gradeGroupEnd; g++) {
                    grades.push(g);
                  }
                  grade = `Grade ${grades.join(',')}`;
                }
              }
              
              if (!ageGroups[grade]) {
                ageGroups[grade] = [];
              }
              ageGroups[grade].push(wrestler);
            });
            
            // Create separate classes for each age/grade within this weight class
            Object.entries(ageGroups).forEach(([ageGroup, ageWrestlers]: [string, typeof weightClassWrestlers]) => {
              if (ageWrestlers.length > 0) {
                classes.push({
                  name: `Boys ${weight.toFixed(1)}-${(weight + weightDelta).toFixed(1)} kg ${ageGroup}`,
                  wrestlers: ageWrestlers,
                  minWeight: weight,
                  maxWeight: weight + weightDelta,
                  gender: 'M',
                  ageGroup: ageGroup
                });
              }
            });
          }
        }
      }
      
      // Step 2: Create weight classes for females, then subdivide by age/grade
      if (femaleWrestlers.length > 0) {
        const femaleWeights = femaleWrestlers.map(w => w.weightKg).sort((a, b) => a - b);
        const femaleMinWeight = Math.floor(femaleWeights[0] / weightDelta) * weightDelta;
        const femaleMaxWeight = Math.ceil(femaleWeights[femaleWeights.length - 1] / weightDelta) * weightDelta;
        
        for (let weight = femaleMinWeight; weight <= femaleMaxWeight; weight += weightDelta) {
          const weightClassWrestlers = femaleWrestlers.filter(w => 
            w.weightKg >= weight && w.weightKg < weight + weightDelta
          );
          
          if (weightClassWrestlers.length > 0) {
            // Step 3: Subdivide by age/grade within this weight class
            const ageGroups: { [key: string]: typeof weightClassWrestlers } = {};
            
            weightClassWrestlers.forEach((wrestler: any) => {
              let numericGrade: number | undefined;
              
              if (wrestler.grade !== undefined) {
                if (typeof wrestler.grade === 'number') {
                  numericGrade = wrestler.grade;
                } else if (typeof wrestler.grade === 'string') {
                  const extractedGrade = parseInt(wrestler.grade.replace(/\D/g, ''));
                  if (!isNaN(extractedGrade)) {
                    numericGrade = extractedGrade;
                  }
                }
              } else if (wrestler.age !== undefined && typeof wrestler.age === 'number') {
                // Use age as grade approximation (age + 3 = typical grade)
                numericGrade = wrestler.age + 3;
              }
              
              let grade = 'Unknown';
              if (numericGrade !== undefined) {
                // Group grades by ageDelta ranges
                const gradeGroup = Math.floor((numericGrade - 1) / ageDelta) * ageDelta + 1;
                const gradeGroupEnd = Math.min(gradeGroup + ageDelta - 1, 8); // Cap max grade at 9
                
                // Create grade range label
                if (ageDelta === 1) {
                  grade = `Grade ${numericGrade}`;
                } else {
                  const grades = [];
                  for (let g = gradeGroup; g <= gradeGroupEnd; g++) {
                    grades.push(g);
                  }
                  grade = `Grade ${grades.join(',')}`;
                }
              }
              
              if (!ageGroups[grade]) {
                ageGroups[grade] = [];
              }
              ageGroups[grade].push(wrestler);
            });
            
            // Create separate classes for each age/grade within this weight class
            Object.entries(ageGroups).forEach(([ageGroup, ageWrestlers]: [string, typeof weightClassWrestlers]) => {
              if (ageWrestlers.length > 0) {
                classes.push({
                  name: `Girls ${weight.toFixed(1)}-${(weight + weightDelta).toFixed(1)} kg ${ageGroup}`,
                  wrestlers: ageWrestlers,
                  minWeight: weight,
                  maxWeight: weight + weightDelta,
                  gender: 'F',
                  ageGroup: ageGroup
                });
              }
            });
          }
        }
      }
    } else {
      // Mixed gender classes - create weight classes first, then subdivide by age/grade
      const weights = wrestlers.map(w => w.weightKg).sort((a, b) => a - b);
      const minWeight = Math.floor(weights[0] / weightDelta) * weightDelta;
      const maxWeight = Math.ceil(weights[weights.length - 1] / weightDelta) * weightDelta;
      
      for (let weight = minWeight; weight <= maxWeight; weight += weightDelta) {
        const weightClassWrestlers = wrestlers.filter(w => 
          w.weightKg >= weight && w.weightKg < weight + weightDelta
        );
        
        if (weightClassWrestlers.length > 0) {
          // Step 3: Subdivide by age/grade within this weight class
          const ageGroups: { [key: string]: typeof weightClassWrestlers } = {};
          
          weightClassWrestlers.forEach((wrestler: any) => {
            let numericGrade: number | undefined;
            
            if (wrestler.grade !== undefined) {
              if (typeof wrestler.grade === 'number') {
                numericGrade = wrestler.grade;
              } else if (typeof wrestler.grade === 'string') {
                const extractedGrade = parseInt(wrestler.grade.replace(/\D/g, ''));
                if (!isNaN(extractedGrade)) {
                  numericGrade = extractedGrade;
                }
              }
            } else if (wrestler.age !== undefined && typeof wrestler.age === 'number') {
              // Use age as grade approximation (age + 3 = typical grade)
              numericGrade = wrestler.age + 3;
            }
            
            let grade = 'Unknown';
            if (numericGrade !== undefined) {
              // Group grades by ageDelta ranges
              const gradeGroup = Math.floor((numericGrade - 1) / ageDelta) * ageDelta + 1;
              const gradeGroupEnd = Math.min(gradeGroup + ageDelta - 1, 9); // Cap max grade at 9
              
              // Create grade range label
              if (ageDelta === 1) {
                grade = `Grade ${numericGrade}`;
              } else {
                const grades = [];
                for (let g = gradeGroup; g <= gradeGroupEnd; g++) {
                  grades.push(g);
                }
                grade = `Grade ${grades.join(',')}`;
              }
            }
            
            if (!ageGroups[grade]) {
              ageGroups[grade] = [];
            }
            ageGroups[grade].push(wrestler);
          });
          
          // Create separate classes for each age/grade within this weight class
          Object.entries(ageGroups).forEach(([ageGroup, ageWrestlers]: [string, typeof weightClassWrestlers]) => {
            if (ageWrestlers.length > 0) {
              classes.push({
                name: `${weight.toFixed(1)}-${(weight + weightDelta).toFixed(1)} kg ${ageGroup}`,
                wrestlers: ageWrestlers,
                minWeight: weight,
                maxWeight: weight + weightDelta,
                ageGroup: ageGroup
              });
            }
          });
        }
      }
    }
    
    return classes;
  };

  const weightClasses = createWeightClasses();

  // Initialize adjusted weight classes when entering step 1
  React.useEffect(() => {
    if (currentStep === 1 && adjustedWeightClasses.length === 0) {
      const baseClasses = [...weightClasses];
      const classesWithHistory = applyMoveHistory(baseClasses, wrestlers);
      setAdjustedWeightClasses(classesWithHistory);
    }
  }, [currentStep, weightClasses, adjustedWeightClasses.length, wrestlers]);

  // Recalculate weight classes when age delta changes
  React.useEffect(() => {
    const baseClasses = [...createWeightClasses()];
    const classesWithHistory = applyMoveHistory(baseClasses, wrestlers);
    setAdjustedWeightClasses(classesWithHistory);
  }, [ageDelta, ageRangeSeparation, wrestlers, weightDelta, separateGenders]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, wrestler: any, fromClass: string) => {
    setDraggedWrestler({ ...wrestler, fromClass });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, className: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverClass(className);
  };

  const handleDragLeave = () => {
    setDragOverClass('');
  };

  const handleDrop = (e: React.DragEvent, toClass: string) => {
    e.preventDefault();
    setDragOverClass('');
    
    if (!draggedWrestler || draggedWrestler.fromClass === toClass) return;
    
    console.log(`Moving wrestler: ${draggedWrestler.firstName} ${draggedWrestler.lastName} from group "${draggedWrestler.fromClass}" to group "${toClass}"`);
    
    // Remove wrestler from original class
    const updatedClasses = adjustedWeightClasses.map(cls => {
      if (cls.name === draggedWrestler.fromClass) {
        return {
          ...cls,
          wrestlers: cls.wrestlers.filter(w => 
            !(w.firstName === draggedWrestler.firstName && 
              w.lastName === draggedWrestler.lastName && 
              w.schoolName === draggedWrestler.schoolName)
          )
        };
      }
      return cls;
    });
    
    // Add wrestler to new class
    const finalClasses = updatedClasses.map(cls => {
      if (cls.name === toClass) {
        return {
          ...cls,
          wrestlers: [...cls.wrestlers, draggedWrestler]
        };
      }
      return cls;
    });
    
    setAdjustedWeightClasses(finalClasses);
    setDraggedWrestler(null);
    
    console.log(`Successfully moved ${draggedWrestler.firstName} ${draggedWrestler.lastName} to group "${toClass}"`);
    
    // Store the group change to local memory
    const moveRecord = {
      wrestler: {
        firstName: draggedWrestler.firstName,
        lastName: draggedWrestler.lastName,
        schoolName: draggedWrestler.schoolName,
        weightKg: draggedWrestler.weightKg
      },
      fromGroup: draggedWrestler.fromClass,
      toGroup: toClass,
      timestamp: new Date().toISOString()
    };
    
    try {
      // Get existing move history or create new array
      const existingMoves = localStorage.getItem('wrestlerMoveHistory');
      const moveHistory = existingMoves ? JSON.parse(existingMoves) : [];
      
      // Add the new move record
      moveHistory.push(moveRecord);
      
      // Keep only the last 100 moves to prevent storage bloat
      if (moveHistory.length > 100) {
        moveHistory.splice(0, moveHistory.length - 100);
      }
      
      // Save back to localStorage
      localStorage.setItem('wrestlerMoveHistory', JSON.stringify(moveHistory));
      
      console.log('Stored wrestler move to local memory:', moveRecord);
    } catch (error) {
      console.error('Error storing wrestler move to local memory:', error);
    }
  };

  // Generate round-based matches for round robin tournaments
  const generateRoundRobinMatches = (wrestlers: any[]) => {
    const rounds = [];
    const shuffled = [...wrestlers].sort(() => Math.random() - 0.5);
    
    if (wrestlers.length === 2) {
      // 2 Competitors - Best of 3
      rounds.push({
        roundNumber: 1,
        matches: [{ wrestler1: shuffled[0], wrestler2: shuffled[1], matchNumber: 1 }],
        byes: []
      });
      rounds.push({
        roundNumber: 2,
        matches: [{ wrestler1: shuffled[1], wrestler2: shuffled[0], matchNumber: 2 }],
        byes: []
      });
      rounds.push({
        roundNumber: 3,
        matches: [{ wrestler1: shuffled[0], wrestler2: shuffled[1], matchNumber: 3 }],
        byes: []
      });
    } else if (wrestlers.length === 3) {
      // 3 Competitors - 3 rounds, 1 bye each round
      const [A, B, C] = shuffled;
      rounds.push({
        roundNumber: 1,
        matches: [{ wrestler1: A, wrestler2: B, matchNumber: 1 }],
        byes: [C]
      });
      rounds.push({
        roundNumber: 2,
        matches: [{ wrestler1: A, wrestler2: C, matchNumber: 2 }],
        byes: [B]
      });
      rounds.push({
        roundNumber: 3,
        matches: [{ wrestler1: B, wrestler2: C, matchNumber: 3 }],
        byes: [A]
      });
    } else if (wrestlers.length === 4) {
      // 4 Competitors - 3 rounds, no byes
      const [A, B, C, D] = shuffled;
      rounds.push({
        roundNumber: 1,
        matches: [
          { wrestler1: A, wrestler2: B, matchNumber: 1 },
          { wrestler1: C, wrestler2: D, matchNumber: 2 }
        ],
        byes: []
      });
      rounds.push({
        roundNumber: 2,
        matches: [
          { wrestler1: A, wrestler2: C, matchNumber: 3 },
          { wrestler1: B, wrestler2: D, matchNumber: 4 }
        ],
        byes: []
      });
      rounds.push({
        roundNumber: 3,
        matches: [
          { wrestler1: A, wrestler2: D, matchNumber: 5 },
          { wrestler1: B, wrestler2: C, matchNumber: 6 }
        ],
        byes: []
      });
    } else if (wrestlers.length === 5) {
      // 5 Competitors - 5 rounds, 1 bye each round
      const [A, B, C, D, E] = shuffled;
      rounds.push({
        roundNumber: 1,
        matches: [
          { wrestler1: A, wrestler2: B, matchNumber: 1 },
          { wrestler1: C, wrestler2: D, matchNumber: 2 }
        ],
        byes: [E]
      });
      rounds.push({
        roundNumber: 2,
        matches: [
          { wrestler1: A, wrestler2: C, matchNumber: 3 },
          { wrestler1: B, wrestler2: E, matchNumber: 4 }
        ],
        byes: [D]
      });
      rounds.push({
        roundNumber: 3,
        matches: [
          { wrestler1: A, wrestler2: D, matchNumber: 5 },
          { wrestler1: C, wrestler2: E, matchNumber: 6 }
        ],
        byes: [B]
      });
      rounds.push({
        roundNumber: 4,
        matches: [
          { wrestler1: A, wrestler2: E, matchNumber: 7 },
          { wrestler1: B, wrestler2: D, matchNumber: 8 }
        ],
        byes: [C]
      });
      rounds.push({
        roundNumber: 5,
        matches: [
          { wrestler1: B, wrestler2: C, matchNumber: 9 },
          { wrestler1: D, wrestler2: E, matchNumber: 10 }
        ],
        byes: [A]
      });
    } else if (wrestlers.length === 6) {
      // 6 Competitors - 5 rounds, no byes
      const [A, B, C, D, E, F] = shuffled;
      rounds.push({
        roundNumber: 1,
        matches: [
          { wrestler1: A, wrestler2: F, matchNumber: 1 },
          { wrestler1: B, wrestler2: E, matchNumber: 2 },
          { wrestler1: C, wrestler2: D, matchNumber: 3 }
        ],
        byes: []
      });
      rounds.push({
        roundNumber: 2,
        matches: [
          { wrestler1: A, wrestler2: E, matchNumber: 4 },
          { wrestler1: F, wrestler2: D, matchNumber: 5 },
          { wrestler1: B, wrestler2: C, matchNumber: 6 }
        ],
        byes: []
      });
      rounds.push({
        roundNumber: 3,
        matches: [
          { wrestler1: A, wrestler2: D, matchNumber: 7 },
          { wrestler1: E, wrestler2: C, matchNumber: 8 },
          { wrestler1: F, wrestler2: B, matchNumber: 9 }
        ],
        byes: []
      });
      rounds.push({
        roundNumber: 4,
        matches: [
          { wrestler1: A, wrestler2: C, matchNumber: 10 },
          { wrestler1: D, wrestler2: B, matchNumber: 11 },
          { wrestler1: E, wrestler2: F, matchNumber: 12 }
        ],
        byes: []
      });
      rounds.push({
        roundNumber: 5,
        matches: [
          { wrestler1: A, wrestler2: B, matchNumber: 13 },
          { wrestler1: C, wrestler2: F, matchNumber: 14 },
          { wrestler1: D, wrestler2: E, matchNumber: 15 }
        ],
        byes: []
      });
    }
    
    return rounds;
  };

  // Flatten rounds structure to show all matches with round numbers
  const flattenRoundsToMatches = (weightClass: any) => {
    if (weightClass.rounds && weightClass.rounds.length > 0) {
      // Use rounds structure if available
      const flatMatches: any[] = [];
      weightClass.rounds.forEach((round: any) => {
        round.matches.forEach((match: any) => {
          flatMatches.push({
            ...match,
            roundNumber: round.roundNumber
          });
        });
      });
      return flatMatches;
    } else {
      // Generate rounds and then flatten them
      const generatedRounds = generateRoundRobinMatches(weightClass.wrestlers);
      const flatMatches: any[] = [];
      generatedRounds.forEach((round: any) => {
        round.matches.forEach((match: any) => {
          flatMatches.push({
            ...match,
            roundNumber: round.roundNumber
          });
        });
      });
      return flatMatches;
    }
  };

  // Generate matches based on wrestler count
  const generateMatches = (wrestlers: any[]) => {
    const matches = [];
    const shuffled = [...wrestlers].sort(() => Math.random() - 0.5);
    
    if (wrestlers.length === 2) {
      // Best of 3 - need 3 matches
      matches.push(
        { wrestler1: shuffled[0], wrestler2: shuffled[1], matchNumber: 1 },
        { wrestler1: shuffled[1], wrestler2: shuffled[0], matchNumber: 2 },
        { wrestler1: shuffled[0], wrestler2: shuffled[1], matchNumber: 3 }
      );
    } else if (wrestlers.length === 3) {
      // Round robin - each wrestles each other
      matches.push(
        { wrestler1: shuffled[0], wrestler2: shuffled[1], matchNumber: 1 },
        { wrestler1: shuffled[1], wrestler2: shuffled[2], matchNumber: 2 },
        { wrestler1: shuffled[2], wrestler2: shuffled[0], matchNumber: 3 }
      );
    } else if (wrestlers.length >= 4) {
      // Round robin - each wrestler faces every other wrestler exactly once
      let matchNumber = 1;
      for (let i = 0; i < shuffled.length; i++) {
        for (let j = i + 1; j < shuffled.length; j++) {
          matches.push({
            wrestler1: shuffled[i],
            wrestler2: shuffled[j],
            matchNumber: matchNumber++
          });
        }
      }
    }
    
    return matches;
  };

  const handleViewMatches = (groupName: string) => {
    console.log('handleViewMatches called with:', groupName);
    setSelectedGroupForMatches(groupName);
    setShowMatchesDialog(true);
    console.log('showMatchesDialog set to true');
  };

  // Generate sequenced matches across all weight classes
  const generateSequencedMatches = () => {
    // First, generate rounds for each weight class
    const weightClassesWithRounds = adjustedWeightClasses.map(weightClass => {
      const rounds = generateRoundRobinMatches(weightClass.wrestlers);
      console.log(`Weight class ${weightClass.name}:`, {
        wrestlers: weightClass.wrestlers.length,
        rounds: rounds.length,
        totalMatches: rounds.reduce((sum, r) => sum + r.matches.length, 0)
      });
      return { ...weightClass, rounds };
    });
    
    // Find the maximum number of rounds across all weight classes
    const maxRounds = Math.max(...weightClassesWithRounds.map(wc => wc.rounds?.length || 0));
    console.log('Maximum rounds across all weight classes:', maxRounds);
    
    // Create global sequence: round 1 of all groups, then round 2 of all groups, etc.
    const globalSequence: any[] = [];
    
    for (let roundNum = 1; roundNum <= maxRounds; roundNum++) {
      console.log(`\n=== Global Round ${roundNum} ===`);
      
      // Add all matches from this round across all weight classes
      weightClassesWithRounds.forEach(weightClass => {
        const currentRound = weightClass.rounds?.find(r => r.roundNumber === roundNum);
        
        if (currentRound && currentRound.matches.length > 0) {
          console.log(`Adding ${currentRound.matches.length} matches from ${weightClass.name} round ${roundNum}`);
          globalSequence.push({
            globalRound: roundNum,
            groupName: weightClass.name,
            matches: currentRound.matches,
            byes: currentRound.byes
          });
        } else {
          console.log(`No matches for ${weightClass.name} in round ${roundNum}`);
        }
      });
    }
    
    console.log('\nFinal global sequence:', globalSequence.map(s => ({
      globalRound: s.globalRound,
      groupName: s.groupName,
      matches: s.matches.length
    })));
    
    // Create a single weight group with the global sequence
    return [{
      name: "Tournament Sequence",
      wrestlers: weightClassesWithRounds.flatMap(wc => wc.wrestlers),
      rounds: globalSequence.map((seq, index) => ({
        roundNumber: index + 1,
        matches: seq.matches,
        byes: seq.byes,
        groupName: seq.groupName,
        globalRound: seq.globalRound
      })),
      brackets: [],
      weightClasses: weightClassesWithRounds // Keep original data for reference
    }];
  };

  const handleGenerateDraw = () => {
    if (!selectedWeightClass) return;
    
    const selectedClass = adjustedWeightClasses.find(c => c.name === selectedWeightClass);
    if (!selectedClass) return;

    console.log('\n=== GENERATING DRAW ===');
    console.log('Selected weight class:', selectedWeightClass);
    console.log('Total adjusted weight classes:', adjustedWeightClasses.length);

    // Generate sequenced matches for all weight classes
    const sequencedGroups = generateSequencedMatches();
    
    console.log('\n=== FINAL RESULT ===');
    console.log('Generated sequenced groups:', sequencedGroups.map(g => ({
      name: g.name,
      rounds: g.rounds?.length || 0,
      totalMatches: g.rounds?.reduce((sum, r) => sum + r.matches.length, 0) || 0
    })));
    
    // Log the first few rounds to verify the sequence
    if (sequencedGroups[0]?.rounds) {
      console.log('\nFirst 10 rounds in sequence:');
      sequencedGroups[0].rounds.slice(0, 10).forEach((round, index) => {
        console.log(`Round ${index + 1}: ${round.groupName} - ${round.matches.length} matches`);
      });
    }
    
    setWeightGroups(sequencedGroups);
    setDrawComplete(true);
    setCurrentStep(3);
    
    // Save state to local storage when draw generation is complete
    const stateToSave = {
      currentStep: 3,
      drawComplete: true,
      weightGroups: sequencedGroups,
      wrestlers,
      weightDelta,
      ageDelta,
      separateGenders,
      numMats
    };
    saveStateToLocalStorage(stateToSave);
    
    // Also notify parent component to save to app-level storage
    if (onDrawStateChange) {
      onDrawStateChange(stateToSave);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setSelectedWeightClass('');
    setDrawComplete(false);
    setWeightGroups([]);
  };

  const steps = ['Groupings', 'Manual Adjustments', 'Generate Draw', 'View Brackets'];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Tournament Draw
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Group wrestlers by weight class and generate tournament brackets.
      </Typography>

      {/* Tournament Overview */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Tournament Overview
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <GroupWork sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4">{wrestlers.length}</Typography>
                <Typography variant="body2" color="text.secondary">Total Wrestlers</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Sports sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h4">{weightClasses.length}</Typography>
                <Typography variant="body2" color="text.secondary">Weight Classes</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Shuffle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4">{drawComplete ? weightGroups[0]?.brackets?.length || 0 : 0}</Typography>
                <Typography variant="body2" color="text.secondary">Brackets Generated</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

 

      {/* Stepper */}
      <DrawStepper activeStep={currentStep} steps={steps}></DrawStepper>

      {/* Step Content */}
      <Paper sx={{ p: 3, mb: 4 }}>

        {currentStep === 0 && (
          <GeneralGroupings
            weightClasses={weightClasses}
            weightDelta={weightDelta}
            ageDelta={ageDelta}
            separateGenders={separateGenders}
            showAdvanced={showAdvanced}
            currentStep={currentStep}
            drawComplete={drawComplete}
            onWeightDeltaChange={setWeightDelta}
            onAgeDeltaChange={setAgeDelta}
            onSeparateGendersChange={setSeparateGenders}
            onShowAdvancedChange={setShowAdvanced}
            onContinueClick={() => setCurrentStep(1)}
          />
        )}
        

        {currentStep === 1 && (
          <AdjustGroupings
            adjustedWeightClasses={adjustedWeightClasses}
            dragOverClass={dragOverClass}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onGroupFormatChange={handleGroupFormatChange}
            onRefreshGroup={handleRefreshGroup}
            onBackClick={() => setCurrentStep(0)}
            onContinueClick={handleStep3Entry}
            groupFormats={groupFormats}
            generateMatches={generateMatches}
            flattenRoundsToMatches={flattenRoundsToMatches}
          />
        )}

        {currentStep === 2 && (
          <GenerateDraw
            numMats={numMats}
            setNumMats={setNumMats}
            scheduledMatches={scheduledMatches}
            restTimeAnalysis={restTimeAnalysis}
            onGenerateSchedule={handleScheduleMatches}
            setCurrentStep={setCurrentStep}
            onAddRest={handleAddRest}
            onScheduleReset={handleScheduleReset}
          />
        )}

        {currentStep === 3 && (
          
          
          <Box>

            <ViewBrackets 
              viewMode={viewMode}
              setViewMode={setViewMode}
              handlePrint={handlePrint}
            ></ViewBrackets>

            {viewMode === 'schedule' && scheduledMatches.length > 0 && (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Tournament Schedule - {scheduledMatches.length} total matches across {numMats} mat(s)
                </Alert>

                <Grid container spacing={2}>
                  {Array.from({ length: numMats }, (_, matIndex) => {
                    const matMatches = scheduledMatches.filter(match => match.mat === matIndex + 1);
                    const matGroups = Array.from(new Set(matMatches.map(m => m.weightClass)));
                    
                    return (
                      <Grid item xs={12} md={numMats === 1 ? 12 : numMats === 2 ? 6 : 4} key={matIndex}>
                        <Card variant="outlined" className="print-break">
                          <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                              Mat {matIndex + 1}
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Weight Classes ({matGroups.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {matGroups.map(groupName => (
                                  <Chip 
                                    key={groupName}
                                    label={groupName} 
                                    size="small" 
                                    variant="outlined" 
                                    color="primary"
                                  />
                                ))}
                              </Box>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom>
                              Matches ({matMatches.length}):
                            </Typography>
                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>

                              {matMatches.map((scheduledMatch) => {
                                const isFlagged = restTimeAnalysis?.flaggedMatches?.some(match => match.mat === scheduledMatch.mat && match.boutNumber === scheduledMatch.boutNumber) ?? false;

                                return (
                                <Card 
                                  key={scheduledMatch.boutNumber} 
                                  variant="outlined" 
                                  color={isFlagged ? 'error' : 'default'}
                                  sx={{ 
                                    mb: 2,
                                    border: isFlagged ? '2px solid #f44336' : '1px solid rgba(0, 0, 0, 0.12)',
                                    backgroundColor: isFlagged ? '#ffebee' : 'background.paper'
                                  }}
                                >
                                  {scheduledMatch.weightClass !== "REST PERIOD" && (
                                  <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <Typography variant="subtitle2" color={isFlagged ? 'error' : 'primary'}>
                                        Match #{scheduledMatch.boutNumber}
                                        {isFlagged && (
                                          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                            ⚠️ Insufficient Rest
                                          </Typography>
                                        )}
                                      </Typography>
                                      <Chip 
                                        label={scheduledMatch.weightClass} 
                                        size="small" 
                                        variant="outlined" 
                                        color={isFlagged ? 'error' : 'default'}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                                      <Typography variant="body2" sx={{ display: 'inline' }}>
                                        {scheduledMatch.match.wrestler1.firstName} {scheduledMatch.match.wrestler1.lastName} ({scheduledMatch.match.wrestler1.schoolName || 'Unknown'})
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, display: 'inline' }}>
                                        vs
                                      </Typography>
                                      <Typography variant="body2" sx={{ display: 'inline' }}>
                                        {scheduledMatch.match.wrestler2.firstName} {scheduledMatch.match.wrestler2.lastName} ({scheduledMatch.match.wrestler2.schoolName || 'Unknown'})
                                      </Typography>
                                    </Box>
                                  </CardContent>
                                  )}
                                  {scheduledMatch.weightClass === "REST PERIOD" && (
                                    <CardContent sx={{ p: 2 }}>
                                      <Typography variant="subtitle2" color={isFlagged ? 'error' : 'primary'}>
                                        Match #{scheduledMatch.boutNumber}
                                        {isFlagged && (
                                          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                            ⚠️ Insufficient Rest
                                          </Typography>
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        Rest Period - 5 Minutes
                                      </Typography>
                                    </CardContent>
                                  )}
                                </Card>
                                );
                              })}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {viewMode === 'brackets' && (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Tournament Sequence - Matches organized by rounds across all weight classes
                </Alert>

                <Grid container spacing={2}>
                  {weightGroups.map((group) => (
                    <Grid item xs={12} key={group.name}>
                      <Card variant="outlined" className="print-break">
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                            {group.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {group.rounds?.length || 0} sequenced rounds across all weight classes
                          </Typography>
                          
                          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {group.rounds?.map((round: any) => (
                              <Box key={round.roundNumber} sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1 }}>
                                  Round {round.roundNumber} - {round.groupName}
                                </Typography>
                                {round.matches.map((match: any, matchIndex: number) => (
                                  <Box key={matchIndex} sx={{ 
                                    mb: 1, 
                                    p: 1, 
                                    backgroundColor: 'grey.50', 
                                    borderRadius: 1,
                                    fontSize: '0.75rem'
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="caption" sx={{ display: 'inline' }}>
                                        {match.wrestler1.firstName} {match.wrestler1.lastName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ mx: 1, display: 'inline' }}>
                                        vs
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'inline' }}>
                                        {match.wrestler2.firstName} {match.wrestler2.lastName}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                                {round.byes.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {round.byes.map((byeWrestler: any) => 
                                      `${byeWrestler.firstName} ${byeWrestler.lastName}`
                                    ).join(', ')} is off
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setCurrentStep(2)}>
                Back
              </Button>
              <Button variant="contained" color="success" startIcon={<AssignmentTurnedIn />}>
                Finalize Tournament
              </Button>
            </Box>
          </Box>
        )}

      </Paper>

      {/* Mat Confirmation Dialog */}
      <Dialog open={showMatConfirmationDialog} onClose={handleMatConfirmationCancel}>
        <DialogTitle>Confirm Number of Mats</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You currently have a schedule with {numMats} mat(s). Would you like to change the number of mats?
          </Typography>
          <TextField
            type="number"
            label="Number of Mats"
            value={tempNumMats}
            onChange={(e) => setTempNumMats(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1, max: 10 }}
            sx={{ width: 200 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Note: Changing the number of mats will regenerate the entire schedule.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMatConfirmationCancel}>
            Keep Current ({numMats} mats)
          </Button>
          <Button 
            onClick={handleMatConfirmation} 
            variant="contained"
            color={tempNumMats !== numMats ? "primary" : "secondary"}
          >
            {tempNumMats !== numMats ? `Change to ${tempNumMats} mats` : `Keep ${numMats} mats`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );


};

export default DrawView;
